import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { colors, inr } from "@/theme";

export function ConciergeWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hi! I'm your Garlic concierge. Tell me what you're shopping for — a gift, a vibe, a room — and I'll suggest pieces." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs, open, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    const history = msgs.map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api("/ai/concierge", { method: "POST", auth: false, body: { message: text, history } });
      setMsgs((m) => [...m, { role: "assistant", content: res.reply, products: res.products || [] }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="concierge-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Shopping concierge"
        style={{
          position: "fixed", right: 20, bottom: 88, zIndex: 50, width: 56, height: 56, borderRadius: 999,
          background: colors.brand, color: colors.onBrandPrimary, border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 28px rgba(74,95,69,0.35)",
        }}
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {open && (
        <div
          data-testid="concierge-panel"
          style={{
            position: "fixed", right: 20, bottom: 156, zIndex: 50, width: "min(380px, calc(100vw - 40px))",
            height: 480, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16,
            display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 16px 48px rgba(44,41,37,0.22)",
          }}
        >
          <div style={{ padding: "14px 16px", background: colors.brand, color: colors.onBrandPrimary, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={17} />
            <span className="font-display" style={{ fontSize: 17, fontWeight: 700 }}>Nest Concierge</span>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
                <div style={{
                  padding: "10px 13px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.5,
                  background: m.role === "user" ? colors.brand : colors.surfaceSecondary,
                  color: m.role === "user" ? colors.onBrandPrimary : colors.onSurface,
                  whiteSpace: "pre-wrap",
                }}>{m.content}</div>
                {m.products?.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {m.products.map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} onClick={() => setOpen(false)} data-testid={`concierge-product-${p.id}`}
                        style={{ display: "flex", gap: 10, alignItems: "center", textDecoration: "none", color: colors.onSurface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
                        {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="clamp-1" style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: colors.mutedText }}>{inr(p.price)}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && <div style={{ alignSelf: "flex-start", color: colors.mutedText, fontSize: 13, fontStyle: "italic" }}>thinking…</div>}
          </div>

          <form onSubmit={send} style={{ display: "flex", gap: 8, padding: 12, borderTop: `1px solid ${colors.border}` }}>
            <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about products…"
              style={{ flex: 1, border: `1px solid ${colors.border}`, borderRadius: 999, padding: "10px 14px", fontSize: 13.5, outline: "none", fontFamily: "DM Sans", background: colors.surfaceSecondary }} />
            <button type="submit" data-testid="concierge-send" disabled={busy} style={{ width: 40, height: 40, borderRadius: 999, background: colors.onSurface, color: colors.surface, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
